import appSettings from 'wanderu-config';
import {
    splitTestSelectors,
} from 'wanderu-ducks-split-test';
import Logger from 'wanderu-logger';

const logger = Logger.getLogger('serverApiService');
let SHARED_AUTH_KEY_ACROSS_REQUESTS;
let store;
let visitorId: string;

// NB: This is NOT SSR SAFE! visitorId and the store can be overwritten by concurrent requests
function initService({ visitorId: passedVisitorId }: { visitorId: string }) {
    visitorId = passedVisitorId;
}

function injectStore(passedStore) {
    store = passedStore;
}

function assertResponseHasNoErrors(response) {
    if (!response.error || !response.error.codeKey) {
        return;
    }

    const error = new Error(response.error.codeKey);

    error.code = response.error.codeKey;
    error.responseBody = response;

    throw error;
}

function getHeaders(authToken) {
    const headers = {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
    };

    if (authToken) {
        headers['X-TOKEN'] = authToken;
    }

    return headers;
}

async function get(url, init = {}) {
    const token = await getAuthenticationToken();
    try {
        const response = await fetch(url, {
            method: 'get',
            ...init,
            headers: {
                ...getHeaders(token),
                ...init.headers,
            },
        });

        if (isUnauthorized(response)) {
            clearToken();
            return get(url, init);
        }

        const json = await response.json();

        assertResponseHasNoErrors(json);

        return json;
    } catch (ex) {
        // decorate the error to get more exact reasons on the fail
        logger.warn('server get error', ex, {
            init,
            // Ideally we would log the response headers, but we need to know if the request
            // actually has completed in a way we can get that information.
            resHeaders: ex?.response?.headers,
            tokenPresent: token.length > 0,
            url,
        });
        throw ex;
    }
}

async function post(url, init = {}) {
    const token = await getAuthenticationToken();
    try {
        const response = await fetch(url, {
            method: 'post',
            ...init,
            body: typeof init.body === 'string'
                ? init.body
                : JSON.stringify(init.body),
            headers: {
                ...getHeaders(token),
                ...init.headers,
            },
        });

        if (isUnauthorized(response)) {
            clearToken();
            return post(url, init);
        }

        const json = await response.json();
        assertResponseHasNoErrors(json);

        return json;
    } catch (ex) {
        logger.warn('server post error', ex, {
            init,
            resHeaders: ex?.response?.headers,
            tokenPresent: token.length > 0,
            url,
        });
        throw ex;
    }
}

async function postAnonymous(url, init = {}) {
    try {
        const response = await fetch(url, {
            method: 'post',
            ...init,
            body: typeof init.body === 'string'
                ? init.body
                : JSON.stringify(init.body),
            headers: {
                ...getHeaders(),
                ...init.headers,
            },
        });

        const json = await response.json();
        assertResponseHasNoErrors(json);

        return json;
    } catch (ex) {
        logger.warn('post anonymous', ex, {
            init,
            resHeaders: ex?.response?.headers,
            url,
        });
        throw ex;
    }
}

async function getAuthenticationToken() {
    if (isForcedIntoBackendSplitTest()) {
        clearToken();
    }

    if (!SHARED_AUTH_KEY_ACROSS_REQUESTS) {
        SHARED_AUTH_KEY_ACROSS_REQUESTS = await requestAnonymousAuthToken();
    }

    return SHARED_AUTH_KEY_ACROSS_REQUESTS;
}

async function requestAnonymousAuthToken() {
    const meta = {};
    if (store) {
        meta.splitTestOverrides = splitTestSelectors.selectTestOverrides(store.getState());
    }

    meta.wanderuVisitorID = visitorId;
    try {
        const response = await postAnonymous(buildWapiUrl('auth.json'), {
            body: {
                credentials: {
                    ...appSettings.config.wapi.credentials,
                    type: 'user',
                    username: 'anonymous',
                },
                meta,
            },
        });

        return response.result.token;
    } catch (ex) {
        logger.error('request anonymous auth token', ex, {
            meta,
            resHeaders: ex?.response?.headers,
        });
        throw ex;
    }
}

function clearToken() {
    SHARED_AUTH_KEY_ACROSS_REQUESTS = undefined;
}

function isUnauthorized(response = {}) {
    return response.status === 401;
}

function isForcedIntoBackendSplitTest() {
    if (!store) {
        return false;
    }

    const splitTestOverrides = splitTestSelectors.selectTestOverrides(store.getState());
    return !!Object.keys(splitTestOverrides).length;
}

function buildWapiUrl(path) { // would have put this in wapiservice, but can't have circular references
    return appSettings.config.wapi.urlRoot + path;
}

export default {
    get,
    getAuthenticationToken,
    initService,
    injectStore,
    post,
};
